import type { Task } from "../../tasks/types.js";
import type { RecurrenceRule } from "../types.js";
import type { RecurrenceOverride } from "./types.js";

/**
 * Effective boundary of a thisandfuture override. When the user moves a tile
 * earlier (due_at < ref), the new schedule kicks in at `due_at`, not `ref`.
 * Using ref alone would cause the moved tile to be filtered out of expansion.
 */
export function effectiveBoundary(o: RecurrenceOverride): string {
  if (o.due_at && o.due_at < o.recurrence_id_ref) return o.due_at;
  return o.recurrence_id_ref;
}

export interface EffectiveOccurrence {
  due_at: string;
  duration_minutes: number;
  title: string;
  notes: string;
  priority: Task["priority"];
  custom_color: string | null;
  all_day: boolean;
  /** Effective occurrence status: "done"/"snoozed" if an override set it, else "open". */
  status: "open" | "done" | "snoozed";
  /** Wake time when the occurrence is snoozed; null otherwise. */
  snoozed_until: string | null;
}

/**
 * Return the rrule string in effect at `occIso`. The latest `thisandfuture`
 * override with a non-null rrule whose ref is at-or-before `occIso` wins;
 * otherwise the base rule's rrule applies.
 *
 * `overrides` is expected to be sorted ascending by `recurrence_id_ref` for
 * efficient scanning, but the implementation tolerates unsorted input.
 */
export function resolveEffectiveRrule(
  rule: RecurrenceRule,
  occIso: string,
  overrides: ReadonlyArray<RecurrenceOverride>,
): string {
  let chosen: RecurrenceOverride | null = null;
  for (const o of overrides) {
    if (o.range !== "thisandfuture") continue;
    if (!o.rrule) continue;
    if (effectiveBoundary(o) > occIso) continue;
    if (!chosen || effectiveBoundary(o) > effectiveBoundary(chosen)) chosen = o;
  }
  return chosen?.rrule ?? rule.rrule;
}

/**
 * Compose the effective occurrence at `occIso` from the base task + overrides.
 *
 * Layering, latest-non-null-wins per field:
 *   1. baseline = baseTask fields, due_at = occIso
 *   2. each `thisandfuture` override where ref <= occIso, in chronological order
 *   3. the single `single` override at ref === occIso (if any)
 *
 * Returns `null` if the effective status is `cancelled` (EXDATE-equivalent).
 */
export function applyOverrides(
  baseTask: Task,
  occIso: string,
  overrides: ReadonlyArray<RecurrenceOverride>,
): EffectiveOccurrence | null {
  const eff: EffectiveOccurrence = {
    due_at: occIso,
    duration_minutes: baseTask.duration_minutes,
    title: baseTask.title,
    notes: baseTask.notes,
    priority: baseTask.priority,
    custom_color: baseTask.custom_color,
    all_day: baseTask.all_day,
    status: "open",
    snoozed_until: null,
  };
  let cancelled = false;

  const taf = overrides
    .filter((o) => o.range === "thisandfuture" && effectiveBoundary(o) <= occIso)
    .slice()
    .sort((a, b) => effectiveBoundary(a).localeCompare(effectiveBoundary(b)));
  for (const o of taf) {
    if (o.status === "cancelled") cancelled = true;
    apply(eff, o);
  }

  const single = overrides.find((o) => o.range === "single" && o.recurrence_id_ref === occIso);
  if (single) {
    cancelled = single.status === "cancelled" ? true : cancelled;
    if (single.status === "open" || single.status === "done" || single.status === "snoozed") cancelled = false;
    apply(eff, single);
  }

  if (cancelled) return null;
  return eff;
}

function apply(eff: EffectiveOccurrence, o: RecurrenceOverride): void {
  // thisandfuture's due_at is the new schedule anchor for the segment — expansion
  // already produced occurrences at the new times via the segment dtstart. Only
  // single overrides should overwrite eff.due_at (they target one specific slot).
  if (o.range === "single" && o.due_at !== null) eff.due_at = o.due_at;
  if (o.duration_minutes !== null) eff.duration_minutes = o.duration_minutes;
  if (o.title !== null) eff.title = o.title;
  if (o.notes !== null) eff.notes = o.notes;
  if (o.priority !== null) eff.priority = o.priority;
  if (o.custom_color !== null) eff.custom_color = o.custom_color;
  if (o.all_day !== null) eff.all_day = o.all_day;
  if (o.status === "open" || o.status === "done") {
    eff.status = o.status;
    eff.snoozed_until = null;
  }
  if (o.status === "snoozed") {
    eff.status = "snoozed";
    eff.snoozed_until = o.snoozed_until;
  }
}
